import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, TestBadge } from "@/components/common";
import { OnboardingWizardDialog } from "@/components/OnboardingWizard";
import { PlanInputsFields, NoCommercialPlan, usePlanInfo, buildInputsPayload } from "@/components/PlanInputsFields";
import { GuidedDocumentCreator } from "@/components/GuidedDocumentCreator";
import { HistoricalDocBuilder } from "@/components/HistoricalDocBuilder";
import { IncludeTestToggle } from "@/components/IncludeTestToggle";
import { useIncludeTest, withIncludeTest } from "@/hooks/useIncludeTest";
import { IndianRupee, FileText, ExternalLink, Calculator, Search, AlertTriangle, ReceiptText, Link2, Copy } from "lucide-react";

const inr = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`);
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const backend = process.env.REACT_APP_BACKEND_URL;

const DocTable = ({ kind, docs }) => (
  docs.length === 0 ? <EmptyState icon={FileText} title={`No ${kind} this month`} hint="Documents appear here after verified payments generate them." /> : (
    <>
      {/* Mobile: card list */}
      <div className="md:hidden space-y-2" data-testid={`${kind}-cards`}>
        {docs.map((d) => (
          <div key={d.document_id} className="rounded-md border p-3 space-y-1.5" data-testid={`doc-card-${d.doc_number}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm">{d.doc_number}</span>
              {d.is_test_record && <TestBadge show />}
              <Badge className={`ml-auto ${d.status === "issued" ? "bg-emerald-500/15 text-emerald-600" : "bg-red-500/15 text-red-600"}`}>{d.status}</Badge>
            </div>
            <div className="text-xs text-muted-foreground">{d.doc_date} · {d.doc_type.replaceAll("_", " ")} · {d.product_name}</div>
            <Link className="text-sm underline decoration-dotted hover:text-primary block" to={`/customers/${d.contact_id}`}>
              {d.customer?.name} <span className="font-mono text-[11px] text-muted-foreground">{d.customer?.customer_id}</span>
            </Link>
            <div className="flex items-center gap-2 flex-wrap text-xs">
              {kind === "invoices" && <span>Taxable {inr(d.summary?.taxable_value)} · GST {inr(d.summary?.gst)}</span>}
              <span className="font-semibold text-sm">{inr(d.summary?.total)}</span>
              <Badge variant="outline" className="text-[9px]">{d.email_status}</Badge>
              <Badge variant="outline" className="text-[9px]">{d.whatsapp_status}</Badge>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <a href={`${backend}/api/finance/documents/${d.document_id}/pdf`} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`pdf-mobile-${d.doc_number}`}><ExternalLink className="h-3.5 w-3.5 mr-1" />PDF</Button>
              </a>
              {d.drive_link && <a href={d.drive_link} target="_blank" rel="noreferrer" className="text-xs text-[hsl(var(--info))] hover:underline">Drive</a>}
            </div>
          </div>
        ))}
      </div>
      {/* Desktop: table */}
      <div className="hidden md:block overflow-x-auto">
        <Table data-testid={`${kind}-table`}>
          <TableHeader><TableRow>
            <TableHead>Number</TableHead><TableHead>Date</TableHead><TableHead>Customer</TableHead><TableHead>Product</TableHead><TableHead>Type</TableHead>
            {kind === "invoices" && <><TableHead>Taxable</TableHead><TableHead>GST</TableHead></>}
            <TableHead>Total</TableHead><TableHead>By</TableHead><TableHead>Email</TableHead><TableHead>WhatsApp</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {docs.map((d) => (
              <TableRow key={d.document_id} data-testid={`doc-${d.doc_number}`}>
                <TableCell className="font-medium">{d.doc_number}{d.is_test_record && <TestBadge show />}</TableCell>
                <TableCell className="text-xs">{d.doc_date}</TableCell>
                <TableCell><Link className="underline decoration-dotted hover:text-primary" to={`/customers/${d.contact_id}`}>{d.customer?.name}</Link><div className="text-xs text-muted-foreground">{d.customer?.customer_id}</div></TableCell>
                <TableCell className="text-xs">{d.product_name}</TableCell>
                <TableCell className="text-xs">{d.doc_type.replaceAll("_", " ")}</TableCell>
                {kind === "invoices" && <>
                  <TableCell className="text-xs">{inr(d.summary?.taxable_value)}</TableCell>
                  <TableCell className="text-xs">{inr(d.summary?.gst)}</TableCell>
                </>}
                <TableCell className="font-medium">{inr(d.summary?.total)}</TableCell>
                <TableCell className="text-xs">{d.generated_by_name || d.generated_by}</TableCell>
                <TableCell><Badge variant="outline">{d.email_status}</Badge></TableCell>
                <TableCell><Badge variant="outline">{d.whatsapp_status}</Badge></TableCell>
                <TableCell><Badge className={d.status === "issued" ? "bg-emerald-500/15 text-emerald-600" : "bg-red-500/15 text-red-600"}>{d.status}</Badge></TableCell>
                <TableCell className="whitespace-nowrap">
                  <a href={`${backend}/api/finance/documents/${d.document_id}/pdf`} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" data-testid={`pdf-${d.doc_number}`}><ExternalLink className="h-4 w-4" />PDF</Button></a>
                  {d.drive_link && <a href={d.drive_link} target="_blank" rel="noreferrer" className="text-xs text-[hsl(var(--info))] hover:underline ml-1">Drive</a>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  )
);

/* ======================= OPERATIONAL WORKQUEUE ======================= */
const GstStatementCard = () => {
  const now = new Date();
  const [year, setYear] = useState(String(now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear()));
  const [month, setMonth] = useState("fy");

  const url = `${backend}/api/finance/gst-statement?year=${year}${month !== "fy" ? `&month=${month}` : ""}`;
  return (
    <Card className="lg:col-span-2" data-testid="gst-statement-card">
      <CardHeader className="pb-2"><CardTitle className="text-sm font-heading flex items-center gap-1.5"><ReceiptText className="h-4 w-4" />GST statement — for accountants</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground">Every payment SalesOS collected in the period, cross-checked against what Razorpay actually processed — flags anything found in only one system, or where the amounts disagree.</p>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger data-testid="gst-month-select" className="h-9 w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="fy">Whole financial year (Apr–Mar)</SelectItem>
              {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="gst-year-input" type="number" className="h-9 w-28" value={year} onChange={(e) => setYear(e.target.value)} />
          <a href={url} target="_blank" rel="noreferrer">
            <Button size="sm" data-testid="gst-download-btn"><FileText className="h-4 w-4 mr-1" />Download CSV</Button>
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

const Workqueue = ({ includeTest = false }) => {
  const [wq, setWq] = useState(null);
  const [linkBusy, setLinkBusy] = useState(null);
  const [linkResult, setLinkResult] = useState(null);
  // Round 7 Workstream F - TEST obligations/payments only when an authorised admin includes them.
  const load = useCallback(() => api.get(withIncludeTest("/finance/workqueue", includeTest)).then((r) => setWq(r.data)).catch((e) => toast.error(errDetail(e))), [includeTest]);
  useEffect(() => { load(); }, [load]);
  const verify = async (paymentId, approve) => {
    try {
      await api.post(`/finance/payments/${paymentId}/verify`, { approve });
      toast.success(approve ? "Payment verified" : "Verification rejected"); load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const generateLink = async (o) => {
    setLinkBusy(o.obligation_id);
    try {
      const { data } = await api.post("/finance/payment-links", { obligation_id: o.obligation_id });
      setLinkResult({ short_url: data.short_url, label: `${o.contact_name} — ${o.label}` });
    } catch (e) { toast.error(errDetail(e)); } finally { setLinkBusy(null); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(linkResult.short_url); toast.success("Link copied"); }
    catch { toast.error("Could not copy — select and copy manually"); }
  };
  if (!wq) return null;
  const OblRows = ({ rows, testPrefix }) => rows.length === 0 ? <p className="text-xs text-muted-foreground py-2">Nothing here.</p> : rows.map((o) => (
    <div key={o.obligation_id} className="flex items-center gap-2 p-2 rounded-md border text-sm flex-wrap" data-testid={`${testPrefix}-${o.obligation_id}`}>
      <Link to={`/customers/${o.contact_id}`} className="font-medium hover:underline">{o.contact_name}</Link>
      <span className="font-mono text-[11px] text-muted-foreground">{o.customer_id}</span>
      <Badge variant="outline" className="text-[9px]">{o.product_code}</Badge>
      <span className="text-xs">{o.label}</span>
      <TestBadge show={o.is_test_record} />
      <span className="ml-auto text-xs">Outstanding <b>{inr(o.outstanding)}</b> / {inr(o.expected_amount)}</span>
      {o.due_at && <span className="text-[11px] text-muted-foreground">due {fmtIST(o.due_at)}</span>}
      <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`${testPrefix}-payment-link-${o.obligation_id}`}
        disabled={linkBusy === o.obligation_id} onClick={() => generateLink(o)}>
        <Link2 className="h-3 w-3 mr-1" />{linkBusy === o.obligation_id ? "…" : "Payment link"}
      </Button>
    </div>
  ));
  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="finance-workqueue">
      <GstStatementCard />
      <Dialog open={!!linkResult} onOpenChange={(o) => !o && setLinkResult(null)}>
        <DialogContent data-testid="payment-link-dialog">
          <DialogHeader><DialogTitle>Payment link — {linkResult?.label}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Share this with the customer — WhatsApp, SMS or email. Once Razorpay confirms payment, it's recorded and verified automatically.</p>
            <div className="flex items-center gap-2">
              <code data-testid="payment-link-url" className="flex-1 font-mono text-xs bg-muted px-2 py-1.5 rounded-md truncate">{linkResult?.short_url}</code>
              <Button size="sm" variant="outline" data-testid="payment-link-copy" onClick={copyLink}><Copy className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setLinkResult(null)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Awaiting verification ({wq.pending_verification.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {wq.pending_verification.length === 0 && <p className="text-xs text-muted-foreground">No recorded payments waiting for verification.</p>}
          {wq.pending_verification.map((p) => (
            <div key={p.payment_id} className="flex items-center gap-2 p-2 rounded-md border text-sm flex-wrap" data-testid={`verify-row-${p.payment_id}`}>
              <Link to={`/customers/${p.contact_id}`} className="font-medium hover:underline">{p.contact_name}</Link>
              <span className="font-mono text-[11px] text-muted-foreground">{p.customer_id}</span>
              <TestBadge show={p.is_test_record} />
              <span className="text-xs">{inr(p.amount)} · {p.method} · {p.reference || "no ref"}</span>
              <div className="ml-auto flex gap-1">
                <Button size="sm" className="h-7 text-xs" data-testid={`verify-approve-${p.payment_id}`} onClick={() => verify(p.payment_id, true)}>Verify</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`verify-reject-${p.payment_id}`} onClick={() => verify(p.payment_id, false)}>Reject</Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading text-[hsl(var(--danger))]">Overdue ({wq.overdue.length})</CardTitle></CardHeader>
        <CardContent className="space-y-1.5"><OblRows rows={wq.overdue} testPrefix="overdue" /></CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Due now ({wq.due.length})</CardTitle></CardHeader>
        <CardContent className="space-y-1.5"><OblRows rows={wq.due} testPrefix="due" /></CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Partial payments ({wq.partial.length})</CardTitle></CardHeader>
        <CardContent className="space-y-1.5"><OblRows rows={wq.partial} testPrefix="partial" /></CardContent>
      </Card>
      <Card className="lg:col-span-2">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading flex items-center gap-1.5"><AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" />NPA / Discontinued ({wq.npa.length})</CardTitle></CardHeader>
        <CardContent className="space-y-1.5">
          {wq.npa.length === 0 && <p className="text-xs text-muted-foreground">No NPA or discontinued accounts.</p>}
          {wq.npa.map((p) => (
            <div key={p.plan_id} className="flex items-center gap-2 flex-wrap p-2 rounded-md border text-sm" data-testid={`npa-${p.plan_id}`}>
              <Link to={`/customers/${p.contact_id}`} className="font-medium hover:underline">{p.contact_name}</Link>
              <Badge variant="outline" className="text-[9px]">{p.product_code}</Badge>
              {p.npa && <Badge variant="outline" className="text-[9px] bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))]">NPA</Badge>}
              {p.discontinued && <Badge variant="outline" className="text-[9px]">Discontinued</Badge>}
              <span className="ml-auto text-xs text-muted-foreground break-all">{p.plan_id}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

/* ================= CREATE & CALCULATE (no manual math) ================= */

const CreateCalculate = ({ onCreated }) => {
  const { hasCap } = useAuth();
  const [mode, setMode] = useState("existing"); // existing | new
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [contact, setContact] = useState(null);
  const [opps, setOpps] = useState([]);
  const [oppId, setOppId] = useState("");
  const [inputs, setInputs] = useState({});
  const [totalAgreed, setTotalAgreed] = useState("");
  const [discount, setDiscount] = useState("0");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  // 2026-09 Phase 3A — "New customer" here now launches the same canonical
  // onboarding wizard every other entry point uses (dedup, product,
  // profile, starting stage, automation decision) instead of its own
  // independently-built inline form; only the "resume into this page's
  // own existing-customer flow afterward" glue stays local.
  const onWizardCreated = (data) => {
    setContact(data.contact);
    setMode("existing");
  };

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data)), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    if (!contact) return;
    api.get(`/contacts/${contact.contact_id}/full`).then((r) => {
      const active = r.data.opportunities.filter((o) => o.lifecycle_status === "active");
      setOpps(active);
      if (active[0]) setOppId(active[0].opportunity_id);
    });
  }, [contact]);
  const opp = opps.find((o) => o.opportunity_id === oppId);
  const { info, error: infoError } = usePlanInfo(opp?.product_code);
  useEffect(() => { setQuote(null); setInputs({}); setTotalAgreed(""); setDiscount("0"); }, [oppId]);

  const calculate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/finance/calculate", {
        product_code: opp.product_code, inputs: buildInputsPayload(info, inputs),
        total_agreed: totalAgreed || null, discount: discount || "0",
      });
      setQuote(r.data);
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  const createPlan = async () => {
    setBusy(true);
    try {
      await api.post("/finance/plans", {
        opportunity_id: oppId, inputs: buildInputsPayload(info, inputs),
        total_agreed: totalAgreed ? Number(totalAgreed) : null,
        discount: Number(discount || 0),
      });
      toast.success("Payment plan created — obligations calculated by SalesOS");
      setQuote(null); setContact(null); setQ(""); setTotalAgreed("");
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="finance-create-calc">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading flex items-center gap-1.5"><Calculator className="h-4 w-4" />Create / calculate payment plan</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {!contact && (
            <div className="flex gap-1 rounded-md border p-1 w-fit">
              <Button size="sm" variant={mode === "existing" ? "default" : "ghost"} className="h-7 text-xs" data-testid="fin-mode-existing" onClick={() => setMode("existing")}>Existing customer</Button>
              <Button size="sm" variant={mode === "new" ? "default" : "ghost"} className="h-7 text-xs" data-testid="fin-mode-new" onClick={() => setMode("new")}>New customer</Button>
            </div>
          )}
          {!contact && mode === "new" ? (
            <div className="space-y-2">
              <p className="text-[11px] text-muted-foreground">Opens the same dedup-checked onboarding wizard every other Create-customer entry point uses. Once created, you'll continue here to calculate their payment plan.</p>
              <OnboardingWizardDialog onCreated={onWizardCreated} triggerLabel="Create customer" triggerTestId="fin-nc-create" />
            </div>
          ) : !contact ? (
            <div className="space-y-2">
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2 top-3 text-muted-foreground" />
                <Input data-testid="fin-customer-search" className="pl-7" placeholder="Search customer by name / phone / Customer ID…" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
              {results.map((r) => (
                <button key={r.contact_id} data-testid={`fin-customer-${r.contact_id}`} onClick={() => setContact(r)}
                  className="w-full text-left p-2 rounded-md border text-sm hover:bg-muted flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{r.full_name}</span>
                  <span className="font-mono text-[11px] text-muted-foreground">{r.customer_id}</span>
                  <TestBadge show={r.is_test_record} />
                </button>
              ))}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-sm">{contact.full_name}</span>
                <span className="font-mono text-[11px] text-muted-foreground">{contact.customer_id}</span>
                <Button size="sm" variant="ghost" className="h-6 text-xs ml-auto" onClick={() => { setContact(null); setQuote(null); }}>Change</Button>
              </div>
              <div className="space-y-1"><Label className="text-xs">Opportunity / product</Label>
                <Select value={oppId} onValueChange={setOppId}>
                  <SelectTrigger data-testid="fin-opportunity-select" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{opps.map((o) => <SelectItem key={o.opportunity_id} value={o.opportunity_id}>{o.product_name || o.product_code}</SelectItem>)}</SelectContent>
                </Select></div>
              {opp && !info && infoError && <NoCommercialPlan productCode={opp.product_code} />}
              {info && (
                <div className="space-y-2 rounded-md border p-2.5">
                  <PlanInputsFields info={info} inputs={inputs} onInputsChange={setInputs}
                    totalAgreed={totalAgreed} onTotalAgreedChange={setTotalAgreed}
                    discount={discount} onDiscountChange={setDiscount} showDiscount
                    testIdPrefix="fin-input" />
                  <Button data-testid="fin-calculate-button" size="sm" disabled={busy} onClick={calculate}><Calculator className="h-3.5 w-3.5 mr-1.5" />Calculate</Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Calculated breakdown (SalesOS — no manual math)</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {!quote ? <p className="text-xs text-muted-foreground">Select a customer, enter the variable inputs and press Calculate.</p> : (
            <div className="space-y-2 text-sm" data-testid="fin-quote-panel">
              {(quote.obligations || []).map((o) => (
                <div key={o.key} className="flex items-center justify-between p-2 rounded-md border">
                  <span className="text-xs">{o.label}</span>
                  <b>{inr(o.expected)}</b>
                </div>
              ))}
              {quote.quote && (
                <div className="rounded-md border p-2.5 text-xs space-y-1 bg-muted/40">
                  <p>Package total: <b>{inr(quote.quote.package_total)}</b>
                    {quote.quote.override_applied && <> (raised from calculated default <b>{inr(quote.quote.baseline_total)}</b>)</>}</p>
                  {quote.quote.third_trigger && <p>Third / final instalment trigger: <b>{quote.quote.third_trigger.replaceAll("_", " ")}</b> — not payable until then</p>}
                  {Number(quote.quote.reimbursement_total || 0) > 0 && (
                    <p>Includes <b>{inr(quote.quote.reimbursement_total)}</b> pure-agent reimbursement (no GST) — see line items above</p>
                  )}
                  <p>Taxable value: <b>{inr(quote.quote.taxable_value)}</b> · GST (18% incl.): <b>{inr(quote.quote.gst)}</b></p>
                </div>
              )}
              <Button data-testid="fin-create-plan-button" disabled={busy} onClick={createPlan}>Create payment plan</Button>
              <p className="text-[11px] text-muted-foreground">Creates auditable obligations; payments are then recorded &amp; verified from the customer's 360 → Finance tab or this workqueue.</p>
            </div>
          )}
        </CardContent>
      </Card>
      {contact && opps.length > 0 && (
        <Card className="lg:col-span-2" data-testid="fin-guided-doc-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-heading flex items-center gap-1.5"><ReceiptText className="h-4 w-4" />Generate document</CardTitle></CardHeader>
          <CardContent>
            <p className="text-[11px] text-muted-foreground mb-2">The normal path for a current transaction — choose a recorded payment or instalment and SalesOS builds the receipt/invoice. Official numbering, PDF and the customer's record are all handled automatically.</p>
            <GuidedDocumentCreator contactId={contact.contact_id} opportunities={opps} onGenerated={onCreated} />
          </CardContent>
        </Card>
      )}
      {contact && (
        <div className="lg:col-span-2">
          <HistoricalDocBuilder contact={contact} opps={opps} onCreated={onCreated} canHistoricalMigration={hasCap("finance.historical_migration")} />
        </div>
      )}
    </div>
  );
};

export default function FinancePage() {
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [receipts, setReceipts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const { includeTest, canInclude, setIncludeTest } = useIncludeTest();
  const load = useCallback(() => {
    const qp = `?year=${year}&month=${month}`;
    api.get(withIncludeTest(`/finance/receipts${qp}`, includeTest)).then((r) => setReceipts(r.data)).catch(() => {});
    api.get(withIncludeTest(`/finance/invoices${qp}`, includeTest)).then((r) => setInvoices(r.data)).catch(() => {});
  }, [year, month, includeTest]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="finance-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight flex items-center gap-2"><IndianRupee className="h-6 w-6 text-primary" />Finance</h1>
          <p className="text-sm text-muted-foreground">Operational finance — calculated obligations, payment verification, receipts &amp; tax invoices. Customer-level actions also live on Customer 360 → Finance.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <IncludeTestToggle includeTest={includeTest} canInclude={canInclude} onChange={setIncludeTest} testId="finance-include-test" />
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger data-testid="finance-month-select" className="h-9 w-36"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger data-testid="finance-year-select" className="h-9 w-24"><SelectValue /></SelectTrigger>
            <SelectContent>{[0, 1, 2].map((d) => { const y = String(now.getFullYear() - d); return <SelectItem key={y} value={y}>{y}</SelectItem>; })}</SelectContent>
          </Select>
        </div>
      </div>
      <Tabs defaultValue="workqueue">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger data-testid="finance-tab-workqueue" value="workqueue">Workqueue</TabsTrigger>
          <TabsTrigger data-testid="finance-tab-create" value="create">Create &amp; Calculate</TabsTrigger>
          <TabsTrigger data-testid="finance-tab-receipts" value="receipts">Receipts ({receipts.length})</TabsTrigger>
          <TabsTrigger data-testid="finance-tab-invoices" value="invoices">Invoices ({invoices.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="workqueue" className="mt-3"><Workqueue includeTest={includeTest} /></TabsContent>
        <TabsContent value="create" className="mt-3"><CreateCalculate onCreated={load} /></TabsContent>
        <TabsContent value="receipts" className="mt-3"><Card><CardContent className="pt-4"><DocTable kind="receipts" docs={receipts} /></CardContent></Card></TabsContent>
        <TabsContent value="invoices" className="mt-3"><Card><CardContent className="pt-4"><DocTable kind="invoices" docs={invoices} /></CardContent></Card></TabsContent>
      </Tabs>
    </div>
  );
}
